import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FilterMatchMode } from "primereact/api";
import { Checkbox } from "primereact/checkbox";
import { Column, ColumnSortMetaData } from "primereact/column";
import { DataTable, DataTableFilterMeta, DataTableSortMeta, DataTableStateEvent } from "primereact/datatable";

import { SecurityGroupRoleAssignmentsReadModel, StringReferenceReadModel } from "../../../../../../@api";

import { compareValues } from "../../../../../../utils/functions/compareValues";
import { translationKeys } from "../../../../../models/app-translation-keys";
import { useStore } from "../../../../../stores/StoreContext";
import { TableSkeleton } from "../../../../Shared/LoadingSkeletons/TableSkeleton/TableSkeleton";
import EditSecurityGroupButton from "../EditSecurityGroupButton/EditSecurityGroupButton";
import { ISecurityGroupRoleAssignments } from "../PermissionsTab";

interface IPermissionCheckboxTableProps {
  securityGroups: ISecurityGroupRoleAssignments[];
  roles: StringReferenceReadModel[];
  isLoading: boolean;
  onReloadTable: () => void;
  globalFilterValue?: string;
  paginatorRows: number[];
  paginatorSelectedRows: number;
}

export const PermissionCheckboxTable: FunctionComponent<IPermissionCheckboxTableProps> = (
  props: IPermissionCheckboxTableProps,
) => {
  const { t } = useTranslation();
  const { userStore } = useStore();

  // Table data
  const [sortedData, setSortedData] = useState<ISecurityGroupRoleAssignments[]>([]);

  // Filters and sorting
  const [filters, setFilters] = useState<DataTableFilterMeta | undefined>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [multiSortMeta, setMultiSortMeta] = useState<DataTableSortMeta[]>([]);

  useEffect(() => {
    const _filters = { ...filters };
    (_filters["global"] as any).value = String(props.globalFilterValue || "").trim();
    setFilters(_filters);
  }, [props.globalFilterValue]);

  useEffect(() => {
    if (!props.isLoading) {
      const newMultiSortMeta: DataTableSortMeta[] = props.roles.map(role => ({
        field: `role.${role.id}`,
        order: 1,
      }));

      const sortedData = dataTableMultiSortFunction(newMultiSortMeta);
      setMultiSortMeta(newMultiSortMeta);
      setSortedData(sortedData);
    }
  }, [props.isLoading, props.securityGroups, props.roles]);

  const dataTableRoleSortFunction = (field: string, order: 1 | 0 | -1): ISecurityGroupRoleAssignments[] => {
    const roleId = field.split(".")[1];
    return [...props.securityGroups].sort((rowA, rowB) => {
      const valueA = rowA.assignedRoleIds?.includes(roleId ?? "");
      const valueB = rowB.assignedRoleIds?.includes(roleId ?? "");

      return valueA === valueB ? 0 : valueA ? 1 * order : -1 * order;
    });
  };

  const dataTableMultiSortFunction = (sortMetaArray: ColumnSortMetaData[] | undefined) => {
    const securityGroups = [...props.securityGroups];
    const configuredSortMetaArray = sortMetaArray?.filter(m => !!m.order);
    if (!configuredSortMetaArray?.length) {
      return securityGroups;
    }

    return securityGroups.sort((rowA: ISecurityGroupRoleAssignments, rowB: ISecurityGroupRoleAssignments) => {
      for (const meta of configuredSortMetaArray) {
        const field = meta.field;
        const order = meta.order ?? 0;

        let comparedResult = 0;
        if (field.includes("role.")) {
          const roleId = field.split(".")[1];
          comparedResult =
            compareValues(rowA.assignedRoleIds?.includes(roleId), rowB.assignedRoleIds?.includes(roleId)) * order;
        } else if (field.includes("securityGroup")) {
          comparedResult = compareValues(rowA.securityGroup.displayName, rowB.securityGroup.displayName) * order;
        } else {
          comparedResult = compareValues((rowA as any)[field], (rowB as any)[field]) * order;
        }

        if (comparedResult !== 0) {
          return comparedResult;
        }
      }

      return 0;
    });
  };

  const AssignedRoleIdColumnBodyElement = (
    rowData: SecurityGroupRoleAssignmentsReadModel,
    role: StringReferenceReadModel,
  ) => (
    <div>
      {!!rowData.securityGroup.id && (
        <Checkbox
          className="role-assignment-checkbox"
          disabled={true}
          checked={rowData.assignedRoleIds.includes(role.id ?? "") ?? false}
        />
      )}
    </div>
  );

  const RoleAssignmentColumnAction = (rowData: SecurityGroupRoleAssignmentsReadModel) => (
    <EditSecurityGroupButton
      data={rowData}
      hide={false}
      roles={props.roles}
      onAddedSecurityGroup={props.onReloadTable}
    />
  );

  const onSort = (event: any) => {
    const { multiSortMeta } = event;
    const sorted = dataTableMultiSortFunction(event.multiSortMeta);
    setSortedData(sorted);
    setMultiSortMeta(multiSortMeta ?? []);
  };

  const tableTemplateMemo = useMemo(() => {
    if (props.isLoading) {
      return <TableSkeleton rows={2} columns={5} />;
    }

    return (
      <DataTable
        key={`${multiSortMeta.length} - ${filters ? Object.keys(filters).length : 0}`}
        value={sortedData}
        filters={filters}
        globalFilterFields={["securityGroup.displayName", "assignedRolesForFilter"]}
        filterDisplay="menu"
        onFilter={(e: DataTableStateEvent) => setFilters(e.filters)}
        scrollable
        rowHover
        scrollHeight="25rem"
        paginator
        rows={props.paginatorSelectedRows}
        rowsPerPageOptions={props.paginatorRows}
        tableStyle={{ minWidth: "100%" }}
        sortMode="multiple"
        multiSortMeta={multiSortMeta}
        onSort={onSort}
        dataKey={data => data.securityGroup.id}
        size="small"
        emptyMessage={t(translationKeys.adminArea.appRoles.noSecurityGroupsFound)}
      >
        <Column
          field="securityGroup"
          sortable
          header={t(translationKeys.adminArea.appRoles.securityGroup.displayName)}
          body={(rowData: SecurityGroupRoleAssignmentsReadModel) => rowData.securityGroup.displayName}
        />

        {props.roles.map(role => (
          <Column
            key={`role.${role.id}`}
            style={{ minWidth: "150px" }}
            field={`role.${role.id}`}
            header={role.displayName}
            sortable
            sortFunction={e => dataTableRoleSortFunction(e.field, e.order ?? 0)}
            body={rowData => AssignedRoleIdColumnBodyElement(rowData, role)}
          />
        ))}

        <Column
          style={{ minWidth: "60px" }}
          headerStyle={{ minWidth: "60px" }}
          frozen
          alignFrozen="right"
          body={RoleAssignmentColumnAction}
        />
      </DataTable>
    );
  }, [sortedData, filters, multiSortMeta, props.isLoading, userStore.currentLanguage]);

  return tableTemplateMemo;
};
